import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useAppSelector } from '../../../../../hooks/redux';
import { Tag } from '../../../../../store/slices/tagsSlice';
import styles from './TripDocumentTagsField.module.scss';

type TripDocumentTagsFieldProps = {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  maxTags?: number;
  disabled?: boolean;
};

const TripDocumentTagsField: FC<TripDocumentTagsFieldProps> = ({ value, onChange, maxTags = 5, disabled = false }) => {
  const { tags } = useAppSelector(state => state.tags);
  const handleTagToggle = (tag: Tag) => {
    if (disabled) return;

    const isSelected = value.some(selectedTag => selectedTag.id === tag.id);
    if (isSelected) {
      onChange(value.filter(selectedTag => selectedTag.id !== tag.id));
      return;
    }

    if (value.length >= maxTags) {
      return;
    }

    onChange([...value, tag]);
  };

  return (
    <Box className={styles.tagsSection}>
      <Typography
        variant="h11Semibold"
        className={styles.sectionLabel}
      >
        Tags
      </Typography>
      <Box className={styles.tagsContainer}>
        {tags && tags.length > 0 ? (
          tags.map(tag => {
            const isSelected = value.some(selectedTag => selectedTag.id === tag.id);

            return (
              <Box
                key={tag.id}
                className={`${styles.tagItem} ${isSelected ? styles.selected : ''} ${disabled ? styles.disabled : ''}`}
                onClick={() => handleTagToggle(tag)}
              >
                <Typography
                  variant="h12Semibold"
                  className={styles.tagText}
                  sx={{
                    backgroundColor: tag.color || '#E0E0E0',
                    color: '#222B3A',
                  }}
                >
                  {tag.name}
                </Typography>
              </Box>
            );
          })
        ) : (
          <Typography
            variant="h12"
            sx={{ color: '#5E6A81' }}
          >
            No tags available
          </Typography>
        )}
      </Box>
      {value.length >= maxTags && (
        <Typography
          variant="h13"
          sx={{ color: '#5E6A81', mt: 1 }}
        >
          Up to {maxTags} tags per document
        </Typography>
      )}
      {value.length > 0 && (
        <Box className={styles.selectedTagsContainer}>
          <Typography
            variant="h12"
            sx={{ color: '#5E6A81', mb: 1 }}
          >
            Selected tags:
          </Typography>
          <Box className={styles.selectedTags}>
            {value.map(tag => (
              <Box
                key={tag.id}
                className={styles.selectedTag}
                sx={{
                  backgroundColor: tag.color || '#E0E0E0',
                  color: '#222B3A',
                }}
              >
                <Typography variant="h12Semibold">{tag.name}</Typography>
                <button
                  type="button"
                  className={styles.removeTagButton}
                  onClick={() => handleTagToggle(tag)}
                  aria-label={`Remove tag ${tag.name}`}
                >
                  <CloseIcon fontSize="inherit" />
                </button>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TripDocumentTagsField;
